// =============================================================================
// src/lib/worklist/rank.js - "Up next" ranking for every worklist.
//
// One rule set, so a care manager, a provider, front desk, a CHW and a manager
// all see work ordered the same way and can see WHY an item is near the top.
// Each rule adds points and (when it fires) a short reason chip.
//
//   rankRow(row, ctx)  -> { score, reasons: [{ text, tone }] }   (max 3 reasons)
//   rankRows(rows, ctx) -> rows sorted by score (stable), each with _rank
//
// ctx: { myId, signals: { [patient_id]: { visit_today_at, in_clinic } }, now }
//
// Rows are the worklist_v shape (item_type, item_subtype, severity, due_at,
// sort_weight, risk_tier, metadata, claimed_by, is_stuck). CHW action items are
// adapted to the same shape by the caller.
// =============================================================================

const DAY = 86400000;

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch (e) { return ""; }
}

export function rankRow(row, ctx = {}) {
  const now = ctx.now || Date.now();
  const md = row.metadata || {};
  const sig = (ctx.signals && row.patient_id && ctx.signals[row.patient_id]) || null;
  const out = []; // { pts, text, tone }
  const add = (pts, text, tone) => out.push({ pts, text, tone: tone || "neutral" });

  // Base: the source view's own weighting (40..90), scaled down so the explicit
  // rules below decide close calls.
  let score = (Number(row.sort_weight) || 50) / 2;

  // Field work a care manager sent (CHW lists).
  if (md.dispatched) add(30, "Dispatched to you", "info");
  if (md.utr) add(20, "Unable to reach", "warn");

  // Clinical urgency first.
  if (row.item_type === "result_ack" && row.item_subtype === "critical") add(100, "Critical result", "danger");
  else if (row.item_type === "result_ack" && row.item_subtype === "abnormal") add(30, "Abnormal result", "warn");
  if (row.item_type === "escalation" && md.urgency === "Urgent") add(80, "Urgent escalation", "danger");
  if (md.priority === "Urgent" || md.priority === "urgent") add(30, "Urgent", "danger");
  else if (md.priority === "High" || md.priority === "high") add(15, "High priority", "warn");

  // Time pressure.
  const due = row.due_at ? new Date(row.due_at).getTime() : null;
  if (row.severity === "overdue") {
    const days = due ? Math.max(0, Math.floor((now - due) / DAY)) : 0;
    add(40 + Math.min(days, 10) * 3, days >= 1 ? "Overdue " + days + "d" : "Overdue", "danger");
  } else if (row.severity === "due_today") {
    add(25, "Due today", "warn");
  } else if (row.severity === "due_soon") {
    add(10, "Due soon", "neutral");
  }
  if (row.item_type === "month_contact" && md.days_left != null) {
    add(md.days_left <= 2 ? 30 : 15, "Month closes in " + md.days_left + "d", "warn");
  }
  if (row.item_type === "rooming" && md.arrived_at) {
    const mins = Math.floor((now - new Date(md.arrived_at).getTime()) / 60000);
    if (mins >= 10) add(Math.min(mins, 60), "Waiting " + mins + " min", mins >= 20 ? "danger" : "warn");
  }

  // No-show risk (Front Desk call list): the score's own top reason.
  if (row.item_type === "confirm_call" && md.risk_score) {
    const top = Array.isArray(md.risk_reasons) && md.risk_reasons[0] ? md.risk_reasons[0].text : "High no-show risk";
    add(Math.min(30, Math.round(md.risk_score / 3)), top, "danger");
  }

  // The patient is here or coming in today.
  if (sig && sig.in_clinic) add(35, "In clinic now", "info");
  else if (sig && sig.visit_today_at) add(20, "Visit today " + fmtTime(sig.visit_today_at), "info");

  // Risk.
  if (row.risk_tier === "Tier 5" || row.risk_tier === "Tier 6") add(15, "High risk (" + row.risk_tier + ")", "warn");
  else if (row.risk_tier === "Tier 4") add(8, "Rising risk", "neutral");

  // Ownership.
  if (row.is_stuck) {
    const days = due ? Math.max(0, Math.floor((now - due) / DAY)) : 0;
    add(20, "No owner " + days + "d", "danger");
  }
  if (ctx.myId && row.claimed_by === ctx.myId) add(10, "You claimed this", "teal");
  else if (row.claimed_by && ctx.myId && row.claimed_by !== ctx.myId) score -= 15; // someone else has it

  for (const r of out) score += r.pts;
  const reasons = out
    .filter((r) => r.text)
    .sort((a, b) => b.pts - a.pts)
    .slice(0, 3)
    .map((r) => ({ text: r.text, tone: r.tone }));
  return { score: Math.round(score), reasons };
}

export function rankRows(rows, ctx = {}) {
  const now = Date.now();
  return (rows || [])
    .map((r, i) => ({ r, i, k: rankRow(r, { ...ctx, now }) }))
    .sort((a, b) => (b.k.score - a.k.score) || (a.i - b.i))
    .map((x) => ({ ...x.r, _rank: x.k }));
}

// Tone -> Badge variant used by the chips.
export const TONE_VARIANT = { danger: "red", warn: "amber", info: "blue", teal: "teal", neutral: "neutral" };
